import { applyPigmentDocumentMutation, isPigmentRevisionedDocument, } from "./document.js";
import { validatePigmentDocument, } from "./manifest.js";
import { canonicalPigmentJson } from "./publication.js";
const CHROME_SLOTS = ["announcement", "header", "footer"];
function flattenDocument(document) {
    const byId = new Map();
    const preorder = [];
    const visit = (nodes, root, parentId, depth) => {
        nodes.forEach((node, index) => {
            const entry = { node, root, parentId, index, depth };
            byId.set(node.id, entry);
            preorder.push(entry);
            visit(node.children ?? [], root, node.id, depth + 1);
        });
    };
    visit(document.blocks ?? [], { kind: "blocks" }, null, 0);
    for (const slot of CHROME_SLOTS) {
        const override = document.chrome?.[slot];
        if (override?.mode === "custom")
            visit(override.blocks ?? [], { kind: "chrome", slot }, null, 0);
    }
    return { byId, preorder };
}
function sameRoot(left, right) {
    return left.kind === right.kind && (left.kind === "blocks" || (right.kind === "chrome" && left.slot === right.slot));
}
function sameJson(left, right) {
    if (left === undefined || right === undefined)
        return left === right;
    return canonicalPigmentJson(left) === canonicalPigmentJson(right);
}
function desiredPlacement(entry, includeIndex = true) {
    return {
        root: entry.root.kind === "blocks" ? { kind: "blocks" } : { kind: "chrome", slot: entry.root.slot },
        parentId: entry.parentId,
        ...(includeIndex ? { index: entry.index } : {}),
    };
}
function insertionSkeleton(node) {
    const cloned = structuredClone(node);
    if (cloned.children !== undefined)
        cloned.children = [];
    return cloned;
}
function siblingCountFor(document, entry) {
    if (entry.parentId !== null)
        return flattenDocument(document).byId.get(entry.parentId)?.node.children?.length ?? 0;
    if (entry.root.kind === "blocks")
        return document.blocks?.length ?? 0;
    const override = document.chrome?.[entry.root.slot];
    return override?.mode === "custom" ? override.blocks?.length ?? 0 : null;
}
function failure(validation, code, message, details = { path: "$" }) {
    return { ok: false, validation, diagnostics: [{ code, message, ...details }] };
}
function inaccessibleChromeBlocks(document) {
    for (const slot of CHROME_SLOTS) {
        const override = document.chrome?.[slot];
        if (override?.mode !== "custom" && (override?.blocks?.length ?? 0) > 0)
            return slot;
    }
    return null;
}
/**
 * Compile an exact desired Pigment document into the portable semantic mutation
 * algebra. Success is stronger than planning: the synthesized mutation has
 * already been applied by the canonical executor and its document compared
 * byte-for-byte (under canonical JSON ordering) with `desired`.
 *
 * Recovery import is intentionally not used here. A desired state which cannot
 * be expressed without replacing stable identity receives a typed refusal.
 */
export function reconcilePigmentDocument(input) {
    const validation = validatePigmentDocument(input.desired, input.manifest);
    if (!validation.ok) {
        const invalidManifest = validation.diagnostics.find((entry) => entry.code === "invalid_manifest");
        if (invalidManifest)
            return failure(validation, "invalid_manifest", invalidManifest.message, { path: invalidManifest.path });
        const duplicate = validation.diagnostics.find((entry) => entry.code === "duplicate_block_id");
        if (duplicate) {
            return failure(validation, "ambiguous_identity", duplicate.message, {
                path: duplicate.path,
                ...(duplicate.blockId === undefined ? {} : { blockId: duplicate.blockId }),
            });
        }
        const first = validation.diagnostics.find((entry) => entry.severity === "error");
        return failure(validation, "invalid_desired", first?.message ?? "Desired document is invalid.", { path: first?.path ?? "$" });
    }
    if (!isPigmentRevisionedDocument(input.base)) {
        return failure(validation, "invalid_base", "Base must be a revisioned Pigment document with globally unique stable IDs.");
    }
    const mutation = { expectedRevision: input.base.revision, operations: [] };
    if (sameJson(input.base.document, input.desired)) {
        return { ok: true, mutation, predictedDiff: [], validation };
    }
    const inaccessibleBase = inaccessibleChromeBlocks(input.base.document);
    const inaccessibleDesired = inaccessibleChromeBlocks(input.desired);
    if (inaccessibleBase !== null || inaccessibleDesired !== null) {
        const slot = inaccessibleBase ?? inaccessibleDesired;
        return failure(validation, "unrepresentable", "Blocks attached to a non-custom chrome slot are not writable semantic state. Make the slot custom or remove the legacy blocks through recovery import.", { path: `$.chrome.${slot}.blocks` });
    }
    const baseFlat = flattenDocument(input.base.document);
    const desiredFlat = flattenDocument(input.desired);
    for (const [id, desired] of desiredFlat.byId) {
        const base = baseFlat.byId.get(id);
        if (base && (base.node.type !== desired.node.type || base.node.slot !== desired.node.slot)) {
            return failure(validation, "unrepresentable", "A stable block ID cannot change block type or named child slot through the current semantic algebra.", { path: "$", blockId: id });
        }
    }
    let working = input.base.document;
    let synthesisFailure = null;
    const append = (operation) => {
        const operationIndex = mutation.operations.length;
        const applied = applyPigmentDocumentMutation({ revision: 0, document: working }, { expectedRevision: 0, operations: [operation] });
        if (!applied.ok) {
            const conflict = applied.conflict;
            synthesisFailure = failure(validation, conflict.code === "revision_mismatch" ? "revision_conflict" : "self_application_failed", `Synthesized operation was refused by the canonical executor: ${conflict.code}.`, { path: "$", operationIndex });
            return false;
        }
        mutation.operations.push(operation);
        working = applied.state.document;
        return true;
    };
    if (!sameJson(input.base.document.theme, input.desired.theme)) {
        if (input.desired.theme === undefined) {
            if (!append({ op: "reset-theme" }))
                return synthesisFailure;
        }
        else if (!append({ op: "set-theme", theme: input.desired.theme }))
            return synthesisFailure;
    }
    // Destination chrome roots must exist before insert/move operations target them.
    for (const slot of CHROME_SLOTS) {
        const desiredOverride = input.desired.chrome?.[slot];
        const currentOverride = working.chrome?.[slot];
        if (desiredOverride?.mode === "custom" && (currentOverride?.mode !== "custom" || currentOverride.blocks === undefined)) {
            if (desiredOverride.blocks === undefined) {
                return failure(validation, "unrepresentable", "A custom chrome slot must explicitly carry its block list for exact reconciliation.", { path: `$.chrome.${slot}.blocks` });
            }
            if (!append({ op: "set-chrome-mode", slot, mode: "custom" }))
                return synthesisFailure;
        }
    }
    // Converge structure in desired preorder. A parent is therefore already in
    // its destination root before a new descendant is inserted beneath it. This
    // ordering matters when retained identity crosses roots: inserting the child
    // first would name a parent which still belongs to a different root and the
    // canonical executor would correctly refuse that placement.
    //
    // Indices which depend on later desired siblings are temporarily omitted so
    // the operation appends within the correct parent. The exact-order pass after
    // obsolete removal deterministically resolves every final index.
    for (const entry of desiredFlat.preorder) {
        const current = flattenDocument(working).byId.get(entry.node.id);
        const siblingCount = siblingCountFor(working, entry);
        const placement = siblingCount !== null && entry.index <= siblingCount
            ? desiredPlacement(entry)
            : desiredPlacement(entry, false);
        if (!current) {
            if (!append({ op: "insert", block: insertionSkeleton(entry.node), placement }))
                return synthesisFailure;
            continue;
        }
        const exactIndexIsAvailable = siblingCount !== null && entry.index <= siblingCount;
        if (!sameRoot(current.root, entry.root)
            || current.parentId !== entry.parentId
            || (exactIndexIsAvailable && current.index !== entry.index)) {
            if (!append({ op: "move", id: entry.node.id, placement }))
                return synthesisFailure;
        }
    }
    // Kept descendants have already moved to their desired parents, so deleting
    // only the topmost obsolete nodes cannot erase desired identity.
    const beforeRemoval = flattenDocument(working);
    const obsolete = new Set([...beforeRemoval.byId.keys()].filter((id) => !desiredFlat.byId.has(id)));
    for (const entry of beforeRemoval.preorder) {
        if (!obsolete.has(entry.node.id) || (entry.parentId !== null && obsolete.has(entry.parentId)))
            continue;
        if (!append({ op: "remove", id: entry.node.id }))
            return synthesisFailure;
    }
    // Re-evaluate placement after every operation. This handles ancestor
    // inversions and sibling-index shifts without an alternate tree executor.
    for (const desired of desiredFlat.preorder) {
        const current = flattenDocument(working).byId.get(desired.node.id);
        if (!current) {
            return failure(validation, "self_application_failed", "A desired stable block disappeared during synthesis.", { path: "$", blockId: desired.node.id });
        }
        if (!sameRoot(current.root, desired.root) || current.parentId !== desired.parentId || current.index !== desired.index) {
            if (!append({ op: "move", id: desired.node.id, placement: desiredPlacement(desired) }))
                return synthesisFailure;
        }
    }
    // Prop changes are last so structure-dependent diagnostics stay attributable
    // to identity/placement rather than incidental prop order.
    for (const desired of desiredFlat.preorder) {
        const current = flattenDocument(working).byId.get(desired.node.id);
        if (!current)
            return failure(validation, "self_application_failed", "A desired block is missing before prop synthesis.", { path: "$", blockId: desired.node.id });
        const currentProps = (current.node.props ?? {});
        const desiredProps = (desired.node.props ?? {});
        for (const key of Object.keys(currentProps).filter((key) => !Object.hasOwn(desiredProps, key)).sort()) {
            if (!append({ op: "reset", id: desired.node.id, key }))
                return synthesisFailure;
        }
        for (const key of Object.keys(desiredProps).sort()) {
            const refreshed = flattenDocument(working).byId.get(desired.node.id);
            const refreshedProps = (refreshed.node.props ?? {});
            if (!Object.hasOwn(refreshedProps, key) || !sameJson(refreshedProps[key], desiredProps[key])) {
                if (!append({ op: "set", id: desired.node.id, key, value: desiredProps[key] }))
                    return synthesisFailure;
            }
        }
    }
    // Switching away from custom is intentionally last: the executor proves the
    // slot is empty, preventing a mode edit from becoming implicit block loss.
    for (const slot of CHROME_SLOTS) {
        const desiredOverride = input.desired.chrome?.[slot];
        const currentOverride = working.chrome?.[slot];
        const desiredMode = desiredOverride?.mode;
        if (desiredMode !== currentOverride?.mode || (desiredMode !== "custom" && !sameJson(desiredOverride, currentOverride))) {
            if (desiredMode === undefined) {
                return failure(validation, "unrepresentable", "Removing an explicit chrome mode is not part of the ordinary semantic algebra.", { path: `$.chrome.${slot}.mode` });
            }
            if (!append({ op: "set-chrome-mode", slot, mode: desiredMode }))
                return synthesisFailure;
        }
    }
    const applied = applyPigmentDocumentMutation(input.base, mutation);
    if (!applied.ok) {
        return failure(validation, applied.conflict.code === "revision_mismatch" ? "revision_conflict" : "self_application_failed", `Final self-application failed: ${applied.conflict.code}.`);
    }
    if (!sameJson(applied.state.document, input.desired)) {
        return failure(validation, "unrepresentable", "The desired document differs in representational details which the semantic operation algebra cannot reproduce exactly.");
    }
    return { ok: true, mutation, predictedDiff: applied.diff, validation };
}
